import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { HullState, Hull, Armor } from "./types";
import type { HullConfiguration, SpecializedHull, AdditionalHull, HullOption } from "../../types/hull";

const initialState: HullState = {
  shipSize: 100,
  techLevel: 12,
  hull: null,
  armor: null,
  isLoading: false,
  error: null,
};

const hullSlice = createSlice({
  name: "hull",
  initialState,
  reducers: {
    // Ship size and tech level
    setShipSize: (state, action: PayloadAction<number>) => {
      state.shipSize = action.payload;
      state.error = null;
    },
    
    setTechLevel: (state, action: PayloadAction<number>) => {
      state.techLevel = action.payload;
      state.error = null;
    },

    // Hull configuration
    setHullConfiguration: (state, action: PayloadAction<Partial<Hull>>) => {
      if (!state.hull) {
        state.hull = {
          size: state.shipSize,
          cost: 0,
          ...action.payload,
        };
      } else {
        state.hull = { ...state.hull, ...action.payload };
      }
      state.error = null;
    },

    setHullConfigurationType: (state, action: PayloadAction<HullConfiguration>) => {
      if (!state.hull) {
        state.hull = {
          size: state.shipSize,
          cost: 0,
          configuration: action.payload,
        };
      } else {
        state.hull.configuration = action.payload;
      }
      state.error = null;
    },

    // Specialized hull
    setSpecializedHull: (state, action: PayloadAction<Partial<Hull>>) => {
      if (!state.hull) {
        state.hull = {
          size: state.shipSize,
          cost: 0,
          ...action.payload,
        };
      } else {
        state.hull = { ...state.hull, ...action.payload };
      }
      state.error = null;
    },

    setSpecializedHullType: (state, action: PayloadAction<SpecializedHull | null>) => {
      if (!state.hull) {
        state.hull = {
          size: state.shipSize,
          cost: 0,
          specializedType: action.payload || undefined,
        };
      } else {
        state.hull.specializedType = action.payload || undefined;
      }
      state.error = null;
    },

    // Additional hull
    setAdditionalHull: (state, action: PayloadAction<Partial<Hull>>) => {
      if (!state.hull) {
        state.hull = {
          size: state.shipSize,
          cost: 0,
          ...action.payload,
        };
      } else {
        state.hull = { ...state.hull, ...action.payload };
      }
      state.error = null;
    },

    setAdditionalHullType: (state, action: PayloadAction<AdditionalHull | null>) => {
      if (!state.hull) {
        state.hull = {
          size: state.shipSize,
          cost: 0,
          additionalType: action.payload || undefined,
        };
      } else {
        state.hull.additionalType = action.payload || undefined;
      }
      state.error = null;
    },

    // Hull options
    setHullOptions: (state, action: PayloadAction<HullOption[]>) => {
      if (!state.hull) {
        state.hull = {
          size: state.shipSize,
          cost: 0,
          options: action.payload,
        };
      } else {
        state.hull.options = action.payload;
      }
      state.error = null;
    },

    addHullOption: (state, action: PayloadAction<HullOption>) => {
      if (!state.hull) {
        state.hull = {
          size: state.shipSize,
          cost: 0,
          options: [action.payload],
        };
      } else {
        state.hull.options = [...(state.hull.options || []), action.payload];
      }
      state.error = null;
    },

    removeHullOption: (state, action: PayloadAction<string>) => {
      if (state.hull?.options) {
        state.hull.options = state.hull.options.filter(
          (option: any) => option.type !== action.payload
        );
      }
      state.error = null;
    },

    updateHullOption: (state, action: PayloadAction<{ type: string; option: Partial<HullOption> }>) => {
      if (state.hull?.options) {
        const index = state.hull.options.findIndex((option: any) => option.type === action.payload.type);
        if (index !== -1) {
          state.hull.options[index] = { ...state.hull.options[index], ...action.payload.option };
        }
      }
      state.error = null;
    },

    // Armor
    setArmor: (state, action: PayloadAction<Armor | null>) => {
      state.armor = action.payload;
      state.error = null;
    },

    updateArmor: (state, action: PayloadAction<Partial<Armor>>) => {
      if (state.armor) {
        state.armor = { ...state.armor, ...action.payload };
      }
      state.error = null;
    },

    // Clear actions
    clearHull: (state) => {
      state.hull = null;
      state.error = null;
    },

    clearArmor: (state) => {
      state.armor = null;
      state.error = null;
    },

    clearAll: (state) => {
      state.hull = null;
      state.armor = null;
      state.error = null;
    },

    // Loading and error states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    // Reset to initial state
    reset: (state) => {
      return initialState;
    },
  },
});

export const {
  setShipSize,
  setTechLevel,
  setHullConfiguration,
  setHullConfigurationType,
  setSpecializedHull,
  setSpecializedHullType,
  setAdditionalHull,
  setAdditionalHullType,
  setHullOptions,
  addHullOption,
  removeHullOption,
  updateHullOption,
  setArmor,
  updateArmor,
  clearHull,
  clearArmor,
  clearAll,
  setLoading,
  setError,
  reset,
} = hullSlice.actions;

export default hullSlice.reducer;
